import React, { useEffect, useState } from "react";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import FourColumnTable from "@/charts/FourColumnTable";
import ScoreComparison from "@/charts/ScoreComparison";
import ComparisonBarChart from "@/charts/ComparisonBarChart";
import CustomDropdown from "@/components/ui/CustomDropdown";
import SectionHeading from "@/components/ui/SectionHeading";
import usePostQuery from "@/hooks/usePostQuery";
import useLlmPlatformFilter from "@/hooks/useLlmPlatformFilter";
import appI18n from "@/i18n";
import { getLocalStorageWithExpiry, showApiError } from "@/utils";
import { CLIENT_ID_KEY } from "@/const";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import "@/pages/page.css";

const { RangePicker } = DatePicker;


// The /api/v2/geo-dashboard endpoints compare a "current" period against a
// "comparison" period. This page lets the user pick both ranges explicitly:
//   Period A -> comparison_period_* (the earlier / baseline range)
//   Period B -> current_period_*    (the later range)
// Backend constraints (see GEODashboardInputV2.validate_*):
//   - each range's inclusive span must be divisible by 7 days
//   - Period A must end on/before Period B starts (no overlap)
//   - if today (UTC) is Sunday, Period B's end date cannot be today
// Additional UI constraint: both periods must span the same number of days, so
// the comparison is like-for-like.
function GEOComparison() {
  const { t, i18n } = useTranslation();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const client_id = getLocalStorageWithExpiry(CLIENT_ID_KEY);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientDetailsError, setClientDetailsError] = useState(null);
  const [isClientDetailsLoading, setIsClientDetailsLoading] = useState(
    Boolean(client_id),
  );

  useEffect(() => {
    if (!client_id) {
      setClientDetails(null);
      setClientDetailsError(null);
      setIsClientDetailsLoading(false);
      return;
    }

    const fetchClientDetails = async () => {
      setIsClientDetailsLoading(true);
      setClientDetailsError(null);
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/v1/client/${client_id}`,
          { credentials: "include" },
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setClientDetails(data);
        setClientDetailsError(null);
      } catch (error) {
        console.error("Error fetching client details:", error);
        showApiError(
          appI18n.t(
            "comparison.clientDetailsUnavailable",
            "Client details could not be loaded. Some brand-specific widgets may be unavailable until this is resolved.",
          ),
        );
        setClientDetails(null);
        setClientDetailsError(error);
      } finally {
        setIsClientDetailsLoading(false);
      }
    };

    fetchClientDetails();
  }, [BACKEND_URL, client_id]);

  const brandName = clientDetails?.data?.own_brand || null;

  // The backend rejects end_date === today when today is Sunday (UTC), so fall
  // back to yesterday in that case.
  const getUtcEndDate = () => {
    const now = new Date();
    const end = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );
    if (now.getUTCDay() === 0) {
      end.setUTCDate(end.getUTCDate() - 1);
    }
    return end;
  };

  // Reinterpret a UTC-anchored calendar day as a local-midnight Date carrying
  // the same Y/M/D, so dayjs 'day' comparisons against picker values aren't
  // thrown off by the viewer's UTC offset.
  const toLocalCalendarDate = (utcDate) =>
    new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
    );

  const endDate = getUtcEndDate();
  const maxAllowedPickerDate = dayjs(toLocalCalendarDate(endDate));

  // Defaults: Period B = the most recent 28 days, Period A = the 28 days before
  // it. A 28-day inclusive span has a 27-day exclusive diff (27 % 7 === 6),
  // which the divisible-by-7 rule accepts.
  const defaultBEnd = maxAllowedPickerDate;
  const defaultBStart = defaultBEnd.subtract(27, "day");
  const defaultAEnd = defaultBStart.subtract(1, "day");
  const defaultAStart = defaultAEnd.subtract(27, "day");

  const [periodA, setPeriodA] = useState([defaultAStart, defaultAEnd]);
  const [periodB, setPeriodB] = useState([defaultBStart, defaultBEnd]);
  const [activePeriodAInput, setActivePeriodAInput] = useState(null);
  const [activePeriodBInput, setActivePeriodBInput] = useState(null);
  // In-progress picker values (from onCalendarChange), which can hold a single
  // endpoint before the range is committed through onChange.
  const [periodADraft, setPeriodADraft] = useState(null);
  const [periodBDraft, setPeriodBDraft] = useState(null);
  // Bumped by Clear to remount the pickers and drop rc-picker's internal draft.
  const [pickerResetKey, setPickerResetKey] = useState(0);

  // Search engine filter, limited to the client's pricing tier
  const {
    llmPlatforms: LLM_Platforms,
    setLlmPlatforms,
    engineOptions,
    isPending: isLlmProvidersPending,
  } = useLlmPlatformFilter(client_id);
  const [brandFilter, setBrandFilter] = useState("all"); // "all" | "branded" | "unbranded"
  const [funnelStageFilter, setFunnelStageFilter] = useState("all"); // "all" | "top" | "middle" | "bottom"
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);

  const isBrandedParam =
    brandFilter === "branded"
      ? true
      : brandFilter === "unbranded"
        ? false
        : undefined;

  const funnelStageParam =
    funnelStageFilter === "all" ? undefined : funnelStageFilter;

  const toggleSidebar = () => setIsPermanentlyExpanded(!isPermanentlyExpanded);

  // Period A's inclusive span in days (null until both ends are picked). Period
  // B is constrained to match it so the two ranges are always like-for-like.
  // Period B is derived from Period A, so it stays disabled until A is picked.
  const isPeriodAComplete = Boolean(periodA?.[0] && periodA?.[1]);

  const periodALengthDays =
    isPeriodAComplete
      ? periodA[1].startOf("day").diff(periodA[0].startOf("day"), "day") + 1
      : null;

  // First click picks either endpoint freely (up to the max allowed date). Once
  // one endpoint is set (info.from), the other is restricted so that:
  //   - Period A: the inclusive span is a multiple of 7 days
  //   - Period B: the inclusive span equals Period A's span
  // info.from is whichever endpoint was picked first, so it can be the start
  // (picking the end now) or the end (picking the start now).
  const makeDisabledDate =
    (
      minStartDate,
      requiredSpanDays,
      activeInputRange,
      draftStartDate,
    ) =>
    (current, info) => {
      if (!current) return false;
      if (current.isAfter(maxAllowedPickerDate, "day")) return true;

      const from = info?.from;
      if (from) {
        const diffDays = current.startOf("day").diff(from.startOf("day"), "day");
        const pickingStart =
          activeInputRange === "start" ||
          (activeInputRange !== "end" && diffDays < 0);
        if (pickingStart ? diffDays > 0 : diffDays < 0) return true;
        if (
          pickingStart &&
          minStartDate &&
          !current.isAfter(minStartDate, "day")
        ) {
          return true;
        }
        const spanDays = Math.abs(diffDays) + 1;
        if (requiredSpanDays) return spanDays !== requiredSpanDays;
        return spanDays % 7 !== 0;
      }

      // No cross-check against the previously committed range here: a new
      // selection replaces both endpoints, and rc-picker also re-validates the
      // submitted pair through this function without `from`, so comparing
      // against the stale range would reject valid new picks.

      // Picking the start date: optionally constrain it to be after a minimum
      // (used for Period B so it can't start before Period A ends).
      if (
        activeInputRange === "start" &&
        minStartDate &&
        !current.isAfter(minStartDate, "day")
      ) {
        return true;
      }
      // Picking the end date first: it must leave room for a start after the
      // minimum that still spans the required length, or the start picker
      // would have no selectable day at all. rc-picker also validates the
      // submitted start date here (no `from`, and activeInputRange may still
      // be "end" from the previous render), so skip the picked start itself.
      if (
        activeInputRange === "end" &&
        minStartDate &&
        !(draftStartDate && current.isSame(draftStartDate, "day")) &&
        current.isBefore(minStartDate.add(requiredSpanDays ?? 1, "day"), "day")
      ) {
        return true;
      }
      return false;
    };

  const isPeriodADateDisabled = makeDisabledDate(
    null,
    null,
    activePeriodAInput,
    periodADraft?.[0],
  );
  const isPeriodBDateDisabled = makeDisabledDate(
    periodA?.[1],
    periodALengthDays,
    activePeriodBInput,
    periodBDraft?.[0],
  );

  // Period selection / validation
  const periodsSelected =
    periodA?.[0] && periodA?.[1] && periodB?.[0] && periodB?.[1];
  const periodsOverlap =
    periodsSelected && !periodB[0].isAfter(periodA[1], "day");
  // Compare exclusive day diffs so both periods cover the same number of days.
  const periodsLengthMismatch =
    periodsSelected &&
    periodA[1].startOf("day").diff(periodA[0].startOf("day"), "day") !==
      periodB[1].startOf("day").diff(periodB[0].startOf("day"), "day");

  let periodsError = null;
  if (periodsSelected && periodsOverlap) {
    periodsError = t(
      "comparison.periodsOverlapError",
      "Period B must start after Period A ends — the two ranges cannot overlap.",
    );
  } else if (periodsLengthMismatch) {
    periodsError = t(
      "comparison.periodsLengthMismatchError",
      "Period A and Period B must span the same number of days.",
    );
  }

  const periodsValid =
    periodsSelected && !periodsOverlap && !periodsLengthMismatch;

  const toUtcIso = (d) =>
    new Date(Date.UTC(d.year(), d.month(), d.date())).toISOString();

  const periods = periodsValid
    ? {
        comparison_period_start_date: toUtcIso(periodA[0]),
        comparison_period_end_date: toUtcIso(periodA[1]),
        current_period_start_date: toUtcIso(periodB[0]),
        current_period_end_date: toUtcIso(periodB[1]),
      }
    : null;

  const withFilters = (extra = {}) =>
    periods && {
      ...periods,
      llm_providers: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
      ...extra,
    };

  const fmt = (d) => {
    if (!d) return "";
    const locale = i18n.resolvedLanguage || i18n.language || "en";
    return d.toDate().toLocaleDateString(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const periodALabel =
    periodA?.[0] && periodA?.[1]
      ? `${fmt(periodA[0])} – ${fmt(periodA[1])}`
      : "Period A";
  const periodBLabel =
    periodB?.[0] && periodB?.[1]
      ? `${fmt(periodB[0])} – ${fmt(periodB[1])}`
      : "Period B";

  const skipApi = !client_id || isClientDetailsLoading || isLlmProvidersPending;

  // Fetch data
  const [citationOverview, citationOverviewIsLoaded, citationOverviewError] =
    usePostQuery(
      `${BACKEND_URL}/api/v2/geo-dashboard/citation-overview`,
      withFilters(),
      { skip: skipApi },
    );

  const [mentionOverview, mentionOverviewIsLoaded, mentionOverviewError] =
    usePostQuery(
      `${BACKEND_URL}/api/v2/geo-dashboard/mention-overview`,
      withFilters({ brand_name: brandName }),
      { skip: skipApi },
    );

  const [citationData, citationDataIsLoaded, citationDataError] = usePostQuery(
    `${BACKEND_URL}/api/v2/geo-dashboard/citation`,
    withFilters(),
    { skip: skipApi },
  );

  const [topCitedPages, topCitedPagesIsLoaded, topCitedPagesError] =
    usePostQuery(
      `${BACKEND_URL}/api/v2/geo-dashboard/citation/top-cited-pages`,
      withFilters({ brand_name: brandName }),
      { skip: skipApi },
    );

  const [mentionData, mentionDataIsLoaded, mentionDataError] = usePostQuery(
    `${BACKEND_URL}/api/v2/geo-dashboard/mention`,
    withFilters(),
    { skip: skipApi },
  );

  // `current_period_*` in the response corresponds to Period B,
  // `previous_period_*` to Period A. The *_count_change fields are the
  // percentage change from Period A to Period B, rendered with the same
  // up/down styling as GEODashboard.
  const formatChange = (change) => {
    if (change === null || change === undefined) return "";
    const num = typeof change === "number" ? change : parseFloat(change);
    if (Number.isNaN(num)) return "";
    return num >= 0
      ? `<span class="text-success"><i class="bi bi-arrow-up-right"></i> ${num}%</span>`
      : `<span class="text-danger"><i class="bi bi-arrow-down-right"></i> ${Math.abs(
          num,
        )}%</span>`;
  };

  const citationRows = (citationData || []).map((item) => ({
    brand: item.brand_name,
    period_a: item.previous_period_citation_count,
    period_b: item.current_period_citation_count,
    change: formatChange(item.citation_count_change),
  }));

  const topCitedPageRows = (topCitedPages || []).map((item) => ({
    url: item.url.replace(/^(https?:\/\/)/, ""),
    period_a: item.previous_period_citation_count,
    period_b: item.current_period_citation_count,
    change: formatChange(item.citation_count_change),
  }));

  const mentionRows = (mentionData || []).map((item) => ({
    brand: item.brand_name,
    period_a: item.previous_period_mention_count,
    period_b: item.current_period_mention_count,
    change: formatChange(item.mention_count_change),
  }));

  // onCalendarChange reports the endpoint that was just picked; the picker then
  // moves focus to the other one, which is what disabledDate must validate.
  const nextActiveRange = (info) =>
    info?.range === "start" ? "end" : info?.range === "end" ? "start" : null;

  const handlePeriodAChange = (dates) => {
    setPeriodA(dates);
    if (dates && dates[0] && dates[1]) {
      setActivePeriodAInput(null);
      const durationDays = dates[1]
        .startOf("day")
        .diff(dates[0].startOf("day"), "day");
      let newBStart = dates[1].add(1, "day");
      let newBEnd = newBStart.add(durationDays, "day");

      if (newBEnd.isAfter(maxAllowedPickerDate, "day")) {
        newBEnd = maxAllowedPickerDate;
        newBStart = newBEnd.subtract(durationDays, "day");
        const newAEnd = newBStart.subtract(1, "day");
        const newAStart = newAEnd.subtract(durationDays, "day");

        setPeriodA([newAStart, newAEnd]);
        setPeriodB([newBStart, newBEnd]);
      } else {
        setPeriodB([newBStart, newBEnd]);
      }
    }
  };

  // Reset both periods so the user can pick a fresh range without the existing
  // dates constraining which days are selectable.
  const handleClearPeriods = () => {
    setPeriodA(null);
    setPeriodB(null);
    setActivePeriodAInput(null);
    setActivePeriodBInput(null);
    setPeriodADraft(null);
    setPeriodBDraft(null);
    setPickerResetKey((key) => key + 1);
  };
  // Include drafts so Clear is available after picking just one endpoint.
  const hasAnyPeriodDate = [periodA, periodB, periodADraft, periodBDraft].some(
    (range) => Boolean(range?.[0] || range?.[1]),
  );

  const engineAllOption = {
    value: "all",
    label: t("dashboard.allSearchEngines", "All search engines"),
  };

  const queryOptions = [
    { value: "all", label: t("dashboard.allQueries", "All queries") },
    {
      value: "branded",
      label: t("dashboard.brandedOnly", "Branded only"),
    },
    {
      value: "unbranded",
      label: t("dashboard.unbrandedOnly", "Unbranded only"),
    },
  ];

  const funnelStageOptions = [
    { value: "all", label: t("dashboard.allFunnelStages", "All funnel stages") },
    { value: "top", label: t("tracking.topFunnel", "Top") },
    { value: "middle", label: t("tracking.middleFunnel", "Middle") },
    { value: "bottom", label: t("tracking.bottomFunnel", "Bottom") },
  ];

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"geo-comparison"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content dashboard-content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {/* Heading */}
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <div>
              <h3 className="h4 fw-bold text-dark mb-0">
                {t("comparison.title", "GEO Comparison")}
              </h3>
              <p className="mt-1 fs-6 text-secondary mb-0">
                {t(
                  "comparison.subtitle",
                  "Compare citation and mention performance between any two time ranges",
                )}
              </p>
            </div>
          </div>

          {/* Filter Area */}
          <div className="geo-filter-panel mb-4">
            <div className="geo-filter-header">
              <div className="geo-filter-heading">
                <span className="geo-filter-icon" aria-hidden="true">
                  <i className="bi bi-sliders2"></i>
                </span>
                <div>
                  <h2>{t("comparison.filterComparison", "Filter comparison")}</h2>
                  <p>{t("comparison.filterHint", "Choose two matching periods and refine the results.")}</p>
                </div>
              </div>
              {periodALengthDays && (
                <span className="geo-filter-duration-badge">
                  <i className="bi bi-calendar3" aria-hidden="true"></i>
                  {periodALengthDays} {t("comparison.days", "days")}
                </span>
              )}
            </div>

            <div className="geo-filter-grid">
              <div className="geo-filter-field">
                <label htmlFor="period-a-picker">
                  <span className="comparison-period-badge period-a">A</span>
                  {t("comparison.baselinePeriod", "Baseline period")}
                </label>
                <RangePicker
                  key={`period-a-${pickerResetKey}`}
                  id="period-a-picker"
                  data-testid="period-a-picker"
                  value={periodA}
                  onOpenChange={(open) => {
                    // Start each session without a draft left over from the last one.
                    if (open) setPeriodADraft(null);
                    else setActivePeriodAInput(null);
                  }}
                  onFocus={(_, info) => setActivePeriodAInput(info?.range ?? null)}
                  onCalendarChange={(dates, __, info) => {
                    setPeriodADraft(dates);
                    setActivePeriodAInput(nextActiveRange(info));
                  }}
                  onChange={handlePeriodAChange}
                  disabledDate={isPeriodADateDisabled}
                  allowClear={false}
                  format="YYYY-MM-DD"
                  separator={
                    <i className="bi bi-arrow-right geo-range-arrow" aria-hidden="true"></i>
                  }
                />
              </div>

              <div className="geo-filter-field">
                <label htmlFor="period-b-picker">
                  <span className="comparison-period-badge period-b">B</span>
                  {t("comparison.comparisonPeriod", "Comparison period")}
                </label>
                <div className="geo-period-input-row">
                  <RangePicker
                    key={`period-b-${pickerResetKey}`}
                    id="period-b-picker"
                    data-testid="period-b-picker"
                    value={periodB}
                    onOpenChange={(open) => {
                      // Start each session without a draft left over from the last one.
                      if (open) setPeriodBDraft(null);
                      else setActivePeriodBInput(null);
                    }}
                    onFocus={(_, info) => setActivePeriodBInput(info?.range ?? null)}
                    onCalendarChange={(dates, __, info) => {
                      setPeriodBDraft(dates);
                      setActivePeriodBInput(nextActiveRange(info));
                    }}
                    onChange={(dates) => {
                      setPeriodB(dates);
                      if (dates?.[0] && dates?.[1]) setActivePeriodBInput(null);
                    }}
                    disabledDate={isPeriodBDateDisabled}
                    disabled={!isPeriodAComplete}
                    allowClear={false}
                    format="YYYY-MM-DD"
                    separator={
                      <i className="bi bi-arrow-right geo-range-arrow" aria-hidden="true"></i>
                    }
                  />
                  <button
                    type="button"
                    className="geo-clear-dates-btn"
                    data-testid="clear-periods-btn"
                    onClick={handleClearPeriods}
                    disabled={!hasAnyPeriodDate}
                    title={t("comparison.clearDates", "Clear dates")}
                    aria-label={t("comparison.clearDates", "Clear dates")}
                  >
                    <i className="bi bi-x-lg" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <div className="geo-filter-field">
                <label>{t("comparison.searchEngine", "Search engine")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  multiple
                  allOption={engineAllOption}
                  placeholder={t("dashboard.selectSearchEngines", "Select search engines")}
                  selectAllLabel={t("common.selectAll", "Select all")}
                  deselectAllLabel={t("common.deselectAll", "Deselect all")}
                  doneLabel={t("common.done", "Done")}
                  options={engineOptions}
                  value={LLM_Platforms}
                  onChange={setLlmPlatforms}
                />
              </div>

              <div className="geo-filter-field">
                <label>{t("comparison.queryType", "Query type")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  options={queryOptions}
                  value={brandFilter}
                  onChange={setBrandFilter}
                />
              </div>

              <div className="geo-filter-field">
                <label>{t("comparison.funnelStage", "Funnel stage")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  options={funnelStageOptions}
                  value={funnelStageFilter}
                  onChange={setFunnelStageFilter}
                />
              </div>
            </div>

            {!periodsSelected && (
              <div className="geo-filter-alert" role="status">
                <i className="bi bi-info-circle me-2"></i>
                {t(
                  "comparison.pickPeriodsHint",
                  "Pick a baseline period to start the comparison.",
                )}
              </div>
            )}

            {periodsError && (
              <div className="geo-filter-alert" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {periodsError}
              </div>
            )}

            {clientDetailsError && (
              <div className="geo-filter-alert" role="alert">
                {t(
                  "comparison.clientDetailsUnavailable",
                  "Client details could not be loaded. Some brand-specific widgets may be unavailable until this is resolved.",
                )}
              </div>
            )}
          </div>

          {/* Overview */}
          <SectionHeading>{t("comparison.overview", "Overview")}</SectionHeading>
          <br />
          <div className="row">
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreComparison
                title={t("dashboard.totalCitations", "Total Citations")}
                icon="bi bi-link-45deg"
                valueA={citationOverview?.previous_period_total_citations}
                valueB={citationOverview?.current_period_total_citations}
                change={citationOverview?.citation_change}
                isLoaded={periodsValid ? citationOverviewIsLoaded : true}
                error={citationOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreComparison
                title={t("dashboard.brandCitationShare", "Brand Citation Share")}
                icon="bi bi-clock-history"
                valueA={
                  citationOverview?.previous_period_share_of_citation != null
                    ? `${citationOverview.previous_period_share_of_citation}%`
                    : "-"
                }
                valueB={
                  citationOverview?.current_period_share_of_citation != null
                    ? `${citationOverview.current_period_share_of_citation}%`
                    : "-"
                }
                change={citationOverview?.share_of_citation_change}
                isLoaded={periodsValid ? citationOverviewIsLoaded : true}
                error={citationOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreComparison
                title={t("dashboard.totalBrandMentions", "Total Brand Mentions")}
                icon="bi bi-globe2"
                valueA={mentionOverview?.previous_period_total_mentions}
                valueB={mentionOverview?.current_period_total_mentions}
                change={mentionOverview?.mention_change}
                isLoaded={periodsValid ? mentionOverviewIsLoaded : true}
                error={mentionOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreComparison
                title={t("dashboard.brandMentionShare", "Brand Mention Share")}
                icon="bi bi-people"
                valueA={
                  mentionOverview?.previous_period_share_of_mention != null
                    ? `${mentionOverview.previous_period_share_of_mention}%`
                    : "-"
                }
                valueB={
                  mentionOverview?.current_period_share_of_mention != null
                    ? `${mentionOverview.current_period_share_of_mention}%`
                    : "-"
                }
                change={mentionOverview?.share_of_mention_change}
                isLoaded={periodsValid ? mentionOverviewIsLoaded : true}
                error={mentionOverviewError}
              />
            </div>
          </div>
          <br />

          {/* Citation Insights */}
          <SectionHeading>
            {t("comparison.citationInsights", "Citation Insights")}
          </SectionHeading>
          <p className="comparison-section-caption mb-0 text-secondary">
            {t(
              "comparison.periodRangeCaption",
              "Period A: {{periodALabel}} \u00b7 Period B: {{periodBLabel}}",
              { periodALabel, periodBLabel },
            )}
          </p>
          <br />
          <div className="row">
            <div className="col-lg-6 mb-4">
              <ComparisonBarChart
                data={citationRows}
                title={t("comparison.citationsByDomain", "Citations by Domain")}
                labelKey="brand"
                valueAKey="period_a"
                valueBKey="period_b"
                seriesALabel={t("comparison.periodA", "Period A")}
                seriesBLabel={t("comparison.periodB", "Period B")}
                isLoaded={periodsValid ? citationDataIsLoaded : true}
                error={citationDataError}
              />
            </div>
            <div className="col-lg-6 mb-4">
              <FourColumnTable
                data={citationRows}
                title={`${t("comparison.citationsByDomain", "Citations by Domain")} \u2014 ${t("comparison.fullListSuffix", "Full list")}`}
                column1Key="brand"
                column2Key="period_a"
                column3Key="period_b"
                column4Key="change"
                column1Title={t("dashboard.brandName", "Brand name")}
                column2Title={t("comparison.periodA", "Period A")}
                column3Title={t("comparison.periodB", "Period B")}
                column4Title={t("dashboard.change", "Change")}
                columnRatio="34-22-22-22"
                columnAlignment="l-r-r-r"
                isLoaded={periodsValid ? citationDataIsLoaded : true}
                error={citationDataError}
                boxHeight="440px"
              />
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12 mb-4">
              <FourColumnTable
                data={topCitedPageRows}
                title={t("comparison.topCitedPages", "Top Cited Pages")}
                column1Key="url"
                column2Key="period_a"
                column3Key="period_b"
                column4Key="change"
                column1Title={t("comparison.url", "URL")}
                column2Title={t("comparison.periodA", "Period A")}
                column3Title={t("comparison.periodB", "Period B")}
                column4Title={t("dashboard.change", "Change")}
                columnRatio="46-18-18-18"
                columnAlignment="l-r-r-r"
                isLoaded={periodsValid ? topCitedPagesIsLoaded : true}
                error={topCitedPagesError}
                boxHeight="440px"
              />
            </div>
          </div>

          {/* Mention Insights */}
          <SectionHeading>
            {t("comparison.mentionInsights", "Mention Insights")}
          </SectionHeading>
          <p className="comparison-section-caption mb-0 text-secondary">
            {t(
              "comparison.periodRangeCaption",
              "Period A: {{periodALabel}} \u00b7 Period B: {{periodBLabel}}",
              { periodALabel, periodBLabel },
            )}
          </p>
          <br />
          <div className="row">
            <div className="col-lg-6 mb-4">
              <ComparisonBarChart
                data={mentionRows}
                title={t("comparison.mentionsByBrand", "Mentions by Brand")}
                labelKey="brand"
                valueAKey="period_a"
                valueBKey="period_b"
                seriesALabel={t("comparison.periodA", "Period A")}
                seriesBLabel={t("comparison.periodB", "Period B")}
                isLoaded={periodsValid ? mentionDataIsLoaded : true}
                error={mentionDataError}
              />
            </div>
            <div className="col-lg-6 mb-4">
              <FourColumnTable
                data={mentionRows}
                title={`${t("comparison.mentionsByBrand", "Mentions by Brand")} \u2014 ${t("comparison.fullListSuffix", "Full list")}`}
                column1Key="brand"
                column2Key="period_a"
                column3Key="period_b"
                column4Key="change"
                column1Title={t("dashboard.brandName", "Brand name")}
                column2Title={t("comparison.periodA", "Period A")}
                column3Title={t("comparison.periodB", "Period B")}
                column4Title={t("dashboard.change", "Change")}
                columnRatio="34-22-22-22"
                columnAlignment="l-r-r-r"
                isLoaded={periodsValid ? mentionDataIsLoaded : true}
                error={mentionDataError}
                boxHeight="440px"
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default GEOComparison;
